import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Setting } from '@app-state';
import {
    enumMapper,
    IUser,
    ModalResultType,
    USER_ICON_MAP,
    USER_ROBOT_ACCESS_FILTER,
    UserRobotAccess,
    UserType,
} from '@models';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import {
    RoleService,
    UserService,
} from '@services';

import { BehaviorSubject } from 'rxjs';

import {
    CheckPermissionsModalComponent,
    UserGroupImportModalComponent,
    UserModalComponent,
} from './modals';

const UserWebAccessFilterValues = [{
    value: true,
    label: 'Enabled',
    translationKey: 'UserWebAccess_Enabled',
}, {
    value: false,
    label: 'Disabled',
    translationKey: 'UserWebAccess_Disabled',
}];


const UserStatusList = [{
    value: true,
    label: 'Active',
    translationKey: 'UserStatus_Active',
}, {
    value: false,
    label: 'Inactive',
    translationKey: 'UserStatus_Inactive',
}];

const USER_ROBOT_ACCESS_KEY = 'UserRobotAccess';

@Component({
    templateUrl: './users.page.html',
    styleUrls: ['users.page.scss'],
    providers: [
        RoleService, {
            provide: GRID_SERVICE,
            useExisting: UserService,
        },
        {
            provide: STATE_HANDLER,
            useValue: urlStateHandler,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UsersPageComponent extends ODataGridComponent<IUser, number> implements OnInit, OnDestroy {
    public readonly loading$ = new BehaviorSubject(false);

    public readonly UserIconMap = USER_ICON_MAP;
    public readonly UserType = UserType;

    public readonly statusList = UserStatusList;

    public readonly webAccess = UserWebAccessFilterValues;
    public readonly isWindowsAuthenticationEnabled = this._appState.settings.get(Setting.isWindowsAuthEnabled);
    public readonly editUserPermissions = ['Users.Edit'].concat(
        !this._appState.settings.get(Setting.isModernFolderSystemEnabled) ? []
            : ['Robots.Create', 'Robots.Edit', 'Robots.Delete'],
    );

    public readonly isViewOnly = !!this._appState.settings.get(Setting.isCloudRPAInstanceEnabled);
    public readonly isMenuExpandable$ = this._appState.user.permissions
        .isGranted('Users.Create & Roles.View | Units.View & Users.View & Roles.View');

    public userTypes = enumMapper(UserType, {
        resourceKey: 'UserType',
    });

    public robotAccess = enumMapper(UserRobotAccess, {
        resourceKey: USER_ROBOT_ACCESS_KEY,
        filterValueMap: USER_ROBOT_ACCESS_FILTER,
    });

    public defaultUserType = this.userTypes
        .find(x => x.value === UserType.User.toString());

    private _userService = this._injector.get<UserService>(UserService);

    ngOnInit() {
        // filter out unavailable types
        if (!this.isWindowsAuthenticationEnabled) {
            const AD_TYPES = [
                UserType[UserType.DirectoryUser],
                UserType[UserType.DirectoryGroup],
            ];
            this.userTypes = this.userTypes.filter(userType =>
                !AD_TYPES.includes(userType.label),
            );

            this.robotAccess = this.robotAccess.filter(robotAccess =>
                robotAccess.label !== UserRobotAccess[UserRobotAccess.Group],
            );
        } else {
            this.defaultUserType = undefined;
        }

        this._expand$.next(['OrganizationUnits']);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    public openModal(type: UserType, model?: IUser) {
        this._modal
            .openModal(UserModalComponent, model, {
                data: {
                    type,
                },
            })
            .afterClosedResult()
            .subscribe(res => {
                switch (res.type) {
                    case ModalResultType.Add:
                        this._refresh$.next();
                        break;
                    case ModalResultType.Update:
                        const user = res.data!;
                        this._grid.dataManager.patchRow(user.id, user);
                        break;
                }
            });
    }

    public checkPermissions(model: IUser) {
        this._modal.openModal(CheckPermissionsModalComponent, model)
            .afterClosedResult()
            .subscribe();
    }

    public openImportModal() {
        this._modal
            .openModal(UserGroupImportModalComponent, {
                roles: undefined,
            })
            .afterClosedResult()
            .subscribe(res => {
                if (res.type !== ModalResultType.Add) { return; }

                this._refresh$.next();
            });
    }

    public setActive(user: IUser, active: boolean) {
        this.loading$.next(true);

        this._userService.setActive(user.id, active)
            .subscribe(() => {
                this.loading$.next(false);
                user.isActive = active;
            },
                () => {
                    this.loading$.next(false);
                });
    }

    public getLoginAttemptsLink(selectedRow: IUser) {
        return `/users/${selectedRow.id}/logins`;
    }

    public get allowDelete(): boolean {
        return this._grid.selectionManager.hasValue() &&
            this._grid.selectionManager.selected.every((u) => this.userCanBeDeleted(u));
    }

    public userCanBeDeleted(user: IUser): boolean {
        const currentUser = this._appState.user.value;
        if (!currentUser) { return false; }

        const isCurrentUser = currentUser.id === user.id;
        return ((user.rolesList == null || user.rolesList.indexOf('Administrator') < 0) && user.type !== UserType.Robot && !isCurrentUser);
    }

    public userCanBeDeactivated(user: IUser): boolean {
        const currentUser = this._appState.user.value;
        if (!currentUser) { return false; }

        const isCurrentUser = currentUser.id === user.id;
        return user.type !== UserType.Robot && !isCurrentUser;
    }

    public robotAccessKey(user: IUser) {
        if (!user.mayHaveRobotSession) {
            return USER_ROBOT_ACCESS_KEY + '_Disabled';
        }

        if (!user.licenseType && user.type === UserType.DirectoryUser) {
            return USER_ROBOT_ACCESS_KEY + '_Group';
        }

        return USER_ROBOT_ACCESS_KEY + '_Enabled';
    }
}
